import axios from 'axios';

// Debug logging for environment
console.log('NODE_ENV:', process.env.NODE_ENV);
console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

// Determine the API base URL based on environment
// In Docker with nginx proxy, use relative path
const getApiBaseUrl = () => {
  // If explicit API URL is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // For Docker Compose with nginx proxy, use relative path
  // Nginx will proxy /api to backend:5000/api
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

console.log('Using API_BASE_URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies for authentication
});

const formatFieldName = (field: string): string => {
  if (!field) return 'Mező';
  return field
    .split('.')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' > ');
};

const extractValidationErrors = (errors: any): string[] => {
  if (!errors || typeof errors !== 'object') {
    return [];
  }

  const messages: string[] = [];
  Object.keys(errors).forEach((field) => {
    const value = errors[field];
    const entries = Array.isArray(value) ? value : [value];
    entries
      .map((entry) => String(entry ?? '').trim())
      .filter((entry) => entry.length > 0)
      .forEach((entry) => {
        messages.push(`${formatFieldName(field)}: ${entry}`);
      });
  });

  return messages;
};

export const getApiErrorMessage = (
  error: any,
  fallbackMessage: string = 'Váratlan hiba történt. Kérjük, próbálja újra.'
): string => {
  const responseData = error?.response?.data;
  const status = error?.response?.status;

  if (typeof responseData === 'string' && responseData.trim().length > 0) {
    return responseData.trim();
  }

  if (responseData && typeof responseData === 'object') {
    const directMessage = responseData.message || responseData.Message;
    if (typeof directMessage === 'string' && directMessage.trim().length > 0) {
      return directMessage.trim();
    }

    const detailMessage = responseData.detail || responseData.Detail;
    if (typeof detailMessage === 'string' && detailMessage.trim().length > 0) {
      return detailMessage.trim();
    }

    const validationMessages = extractValidationErrors(responseData.errors || responseData.Errors);
    if (validationMessages.length > 0) {
      return validationMessages.join('\n');
    }

    const title = responseData.title || responseData.Title;
    if (typeof title === 'string' && title.trim().length > 0) {
      return title.trim();
    }
  }

  if (typeof error?.message === 'string' && error.message.trim().length > 0) {
    return error.message.trim();
  }

  switch (status) {
    case 400:
      return 'Érvénytelen kérés. Ellenőrizze a megadott adatokat.';
    case 401:
      return 'A művelethez bejelentkezés szükséges.';
    case 403:
      return 'Nincs jogosultsága ehhez a művelethez.';
    case 404:
      return 'A kért erőforrás nem található.';
    case 409:
      return 'Ütközés történt a mentés során.';
    case 500:
      return 'Szerverhiba történt. Kérjük, próbálja újra később.';
    default:
      return fallbackMessage;
  }
};

export const getFetchErrorMessage = async (
  response: Response,
  fallbackMessage: string = 'Váratlan hiba történt. Kérjük, próbálja újra.'
): Promise<string> => {
  let parsedBody: any = null;
  let textBody = '';

  try {
    textBody = await response.text();
  } catch {
    textBody = '';
  }

  if (textBody) {
    try {
      parsedBody = JSON.parse(textBody);
    } catch {
      parsedBody = null;
    }
  }

  if (typeof parsedBody === 'string' && parsedBody.trim().length > 0) {
    return parsedBody.trim();
  }

  if (parsedBody && typeof parsedBody === 'object') {
    const directMessage = parsedBody.message || parsedBody.Message;
    if (typeof directMessage === 'string' && directMessage.trim().length > 0) {
      return directMessage.trim();
    }

    const detailMessage = parsedBody.detail || parsedBody.Detail;
    if (typeof detailMessage === 'string' && detailMessage.trim().length > 0) {
      return detailMessage.trim();
    }

    const validationMessages = extractValidationErrors(parsedBody.errors || parsedBody.Errors);
    if (validationMessages.length > 0) {
      return validationMessages.join('\n');
    }

    const title = parsedBody.title || parsedBody.Title;
    if (typeof title === 'string' && title.trim().length > 0) {
      return title.trim();
    }
  }

  if (textBody.trim().length > 0) {
    return textBody.trim();
  }

  return getApiErrorMessage({ response }, fallbackMessage);
};

const toReadableApiError = (error: any, fallbackMessage: string): Error => {
  const readableMessage = getApiErrorMessage(error, fallbackMessage);

  if (error && typeof error === 'object') {
    (error as any).userMessage = readableMessage;
    (error as any).message = readableMessage;
    return error as Error;
  }

  return new Error(readableMessage);
};

export const apiService = {
  async getData(endpoint: string): Promise<any[]> {
    const endpointMap: { [key: string]: string } = {
      ajanlatok: 'Ajanlatok',
      idoraforditasok: 'Idoraforditasok',
      iktatas: 'Iktatas',
      megrendelok: 'Megrendelok',
      munkaszamok: 'Munkaszamok',
    };

    const actualEndpoint = endpointMap[endpoint] || endpoint;
    
    try {
      const response = await api.get(`/${actualEndpoint}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      throw toReadableApiError(error, `Hiba történt a(z) ${endpoint} adatok lekérése során.`);
    }
  },

  async createData(endpoint: string, data: any): Promise<any> {
    const endpointMap: { [key: string]: string } = {
      ajanlatok: 'Ajanlatok',
      idoraforditasok: 'Idoraforditasok',
      iktatas: 'Iktatas',
      megrendelok: 'Megrendelok',
      munkaszamok: 'Munkaszamok',
    };

    const actualEndpoint = endpointMap[endpoint] || endpoint;
    
    try {
      const response = await api.post(`/${actualEndpoint}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error creating ${endpoint}:`, error);
      throw toReadableApiError(error, `Hiba történt a(z) ${endpoint} létrehozása során.`);
    }
  },

  async updateData(endpoint: string, data: any): Promise<any> {
    const endpointMap: { [key: string]: string } = {
      ajanlatok: 'Ajanlatok',
      idoraforditasok: 'Idoraforditasok',
      iktatas: 'Iktatas',
      megrendelok: 'Megrendelok',
      munkaszamok: 'Munkaszamok',
    };

    const actualEndpoint = endpointMap[endpoint] || endpoint;
    
    try {
      const id = data.id;
      if (!id) {
        throw new Error('ID mező hiányzik az adatokból');
      }
      const response = await api.put(`/${actualEndpoint}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating ${endpoint}:`, error);
      throw toReadableApiError(error, `Hiba történt a(z) ${endpoint} frissítése során.`);
    }
  },

  async deleteData(endpoint: string, data: any): Promise<any> {
    const endpointMap: { [key: string]: string } = {
      ajanlatok: 'Ajanlatok',
      idoraforditasok: 'Idoraforditasok',
      iktatas: 'Iktatas',
      megrendelok: 'Megrendelok',
      munkaszamok: 'Munkaszamok',
    };

    const actualEndpoint = endpointMap[endpoint] || endpoint;
    
    try {
      const id = data.id;
      if (!id) {
        throw new Error('ID mező hiányzik az adatokból');
      }
      const response = await api.delete(`/${actualEndpoint}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting ${endpoint}:`, error);
      throw toReadableApiError(error, `Hiba történt a(z) ${endpoint} törlése során.`);
    }
  },

  // Általános HTTP metódusok hozzáadása
  async get(url: string): Promise<any> {
    try {
      const response = await api.get(url);
      return response;
    } catch (error) {
      console.error(`Error GET ${url}:`, error);
      throw toReadableApiError(error, `Hiba történt a kérés feldolgozása során (${url}).`);
    }
  },

  async post(url: string, data?: any): Promise<any> {
    try {
      const response = await api.post(url, data);
      return response;
    } catch (error) {
      console.error(`Error POST ${url}:`, error);
      throw toReadableApiError(error, `Hiba történt a mentés során (${url}).`);
    }
  },

  async put(url: string, data?: any): Promise<any> {
    try {
      const response = await api.put(url, data);
      return response;
    } catch (error) {
      console.error(`Error PUT ${url}:`, error);
      throw toReadableApiError(error, `Hiba történt a frissítés során (${url}).`);
    }
  },

  async patch(url: string, data?: any): Promise<any> {
    try {
      const response = await api.patch(url, data);
      return response;
    } catch (error) {
      console.error(`Error PATCH ${url}:`, error);
      throw toReadableApiError(error, `Hiba történt a részleges frissítés során (${url}).`);
    }
  },

  async delete(url: string): Promise<any> {
    try {
      const response = await api.delete(url);
      return response;
    } catch (error) {
      console.error(`Error DELETE ${url}:`, error);
      throw toReadableApiError(error, `Hiba történt a törlés során (${url}).`);
    }
  },
};
